import {
    Row,
    createColumnHelper,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { useMemo } from 'react';
import Download from '~/icons/iconography/download.svg';
import { ShopTypes } from '~/lib/data-contract';
import { useTranslations } from '~/services/umbraco/hooks';
import { Text } from '~/shared/components/Text';
import { useCurrency } from '~/shared/hooks';
import { Table as TableInfo } from '~/templates/blocks/shared/Table/Table';
import getInternalInvoiceUrl from '../helper/getInvoiceUrl';
import { StyledPrice, StyledText, TableCellLink } from '../styles';
import Status from './Status';
import { useUserProfile } from '~/services/shop/hooks/mit-fog';

const columnHelper = createColumnHelper<ShopTypes.CustomerInfoHeader>();

const select = [10, 50, 100];

export default function Table({ data }: { data: any }) {
    const { data: profile } = useUserProfile();
    const { translate } = useTranslations();
    const formatCurrency = useCurrency(false);

    const columns = useMemo(
        () => [
            columnHelper.accessor('invoiceDate', {
                header: 'Dato',
                cell: (info) => {
                    const value = info.getValue();
                    return (
                        <StyledText>
                            {value === undefined
                                ? ''
                                : new Date(value).toLocaleDateString('en-GB').replace(/\//g, '-')}
                        </StyledText>
                    );
                },
                sortUndefined: -1,
            }),
            columnHelper.accessor('invoiceNumber', {
                header: translate('mit-fog.accountInfo.invoiceNumber'),
                cell: (info) => <Text>{info.getValue()}</Text>,
            }),
            columnHelper.accessor((row) => row.status?.description, {
                id: 'status',
                header: translate('mit-fog.orderDetails.status'),
                cell: (info) => {
                    return <Status status={info.row.original.status} />;
                },
            }),
            columnHelper.accessor('invoiceAmount', {
                header: translate('mit-fog.accountInfo.invoiceAmount'),
                cell: (info) => <StyledPrice>{formatCurrency(info.getValue())}</StyledPrice>,
                meta: {
                    align: 'right',
                },
            }),
            columnHelper.accessor('remainingAmount', {
                header: translate('mit-fog.accountInfo.remainingAmount'),
                cell: (info) => <StyledPrice>{formatCurrency(info.getValue())}</StyledPrice>,
                meta: {
                    align: 'right',
                },
            }),
            columnHelper.accessor('customerReference', {
                header: translate('mit-fog.accountInfo.customerReference'),
                cell: (info) => <Text>{info.getValue()?.toLocaleUpperCase()}</Text>,
            }),
            columnHelper.accessor('dueDate', {
                header: translate('mit-fog.accountInfo.dueDate'),
                cell: (info) => {
                    const value = info.getValue();
                    return (
                        <Text>
                            {value === undefined
                                ? ''
                                : new Date(value).toLocaleDateString('en-GB').replace(/\//g, '-')}
                        </Text>
                    );
                },
            }),
            {
                id: 'link',
                header: translate('mit-fog.download'),
                cell: ({ row }: { row: Row<ShopTypes.CustomerInfoHeader> }) => {
                    const rowData = row.original;

                    const url = getInternalInvoiceUrl(
                        profile?.user?.currentCustomerId,
                        rowData.orderNumber,
                        rowData.invoiceNumber,
                    );

                    if (!rowData.orderNumber || !url) return null;

                    const handleClick = (e: React.MouseEvent) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (url) {
                            window.location.href = url;
                        } else {
                            console.error('Invalid customer ID or order number');
                        }
                    };

                    return (
                        <TableCellLink
                            customIcon={<Download />}
                            href={url}
                            withIcon
                            onClick={handleClick}
                        >
                            {translate('mit-fog.invoice')}
                        </TableCellLink>
                    );
                },
                meta: {
                    isText: true,
                },
            },
        ],
        [translate, formatCurrency, profile],
    );

    const table = useReactTable({
        data: data ?? [],
        columns,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        initialState: {
            pagination: {
                pageSize: select[0],
            },
            sorting: [
                {
                    id: 'invoiceDate',
                    desc: true,
                },
            ],
        },
    });

    // @ts-ignore
    return <TableInfo table={table} data={data ?? []} select={select} />;
}
